/**
 * Formats a date for message grouping
 * Returns "Today", "Yesterday", or formatted date like "15 Dec 2025"
 * @param {string|Date} timestamp - ISO timestamp string or Date object
 * @returns {string} Formatted date string
 */
export const formatMessageDate = (timestamp) => {
    if (!timestamp) return "";

    try {
        const messageDate = new Date(timestamp);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        // Reset time to midnight for date comparison
        const messageDateOnly = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());
        const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

        if (messageDateOnly.getTime() === todayOnly.getTime()) {
            return "Today";
        } else if (messageDateOnly.getTime() === yesterdayOnly.getTime()) {
            return "Yesterday";
        } else {
            // Format as "15 Dec 2025"
            const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            const day = messageDate.getDate();
            const month = months[messageDate.getMonth()];
            const year = messageDate.getFullYear();
            return `${day} ${month} ${year}`;
        }
    } catch (error) {
        console.error("Error formatting date:", error);
        return "";
    }
};

/**
 * Formats a timestamp as relative time like "Now", "1 min ago", or "3 hours ago".
 * @param {string|number|Date} timestamp - ISO timestamp string, Unix timestamp, or Date object
 * @param {string|number|Date} referenceTime - Optional comparison time, defaults to current time
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (timestamp, referenceTime = new Date()) => {
    if (!timestamp) return "";

    try {
        const date = timestamp instanceof Date
            ? timestamp
            : new Date(typeof timestamp === "number" && timestamp < 10000000000 ? timestamp * 1000 : timestamp);
        const now = referenceTime instanceof Date
            ? referenceTime
            : new Date(typeof referenceTime === "number" && referenceTime < 10000000000 ? referenceTime * 1000 : referenceTime);

        if (isNaN(date.getTime()) || isNaN(now.getTime())) return "";

        const diffMs = Math.max(0, now.getTime() - date.getTime());
        const minuteMs = 60 * 1000;
        const hourMs = 60 * minuteMs;
        const dayMs = 24 * hourMs;
        const monthMs = 30 * dayMs;
        const yearMs = 365 * dayMs;

        if (diffMs < minuteMs) return "Now";

        const getLabel = (value, singular, plural = `${singular}s`) => (
            `${value} ${value === 1 ? singular : plural} ago`
        );

        if (diffMs < hourMs) {
            return getLabel(Math.floor(diffMs / minuteMs), "min");
        }

        if (diffMs < dayMs) {
            return getLabel(Math.floor(diffMs / hourMs), "hour");
        }

        if (diffMs < monthMs) {
            return getLabel(Math.floor(diffMs / dayMs), "day");
        }

        if (diffMs < yearMs) {
            return getLabel(Math.floor(diffMs / monthMs), "month");
        }

        return getLabel(Math.floor(diffMs / yearMs), "year");
    } catch (error) {
        console.warn("Error formatting relative time:", error);
        return "";
    }
};

/**
 * Gets the date key for grouping messages by date
 * @param {string|Date} timestamp - ISO timestamp string or Date object
 * @returns {string} Date key in YYYY-MM-DD format
 */
export const getDateKey = (timestamp) => {
    if (!timestamp) return "";

    try {
        const messageDate = new Date(timestamp);
        const year = messageDate.getFullYear();
        const month = String(messageDate.getMonth() + 1).padStart(2, '0');
        const day = String(messageDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    } catch (error) {
        console.error("Error getting date key:", error);
        return "";
    }
};

/**
 * Converts GMT/UTC timestamp to IST (Indian Standard Time)
 * IST is UTC+5:30
 * @param {string|Date} timestamp - ISO timestamp string or Date object
 * @returns {Date} Date object adjusted for IST (for ISO string conversion)
 */
export const convertGMTToIST = (timestamp) => {
    if (!timestamp) return null;

    try {
        let date;
        if (typeof timestamp === 'number') {
            // Heuristic: If timestamp is in seconds (10 digits), convert to milliseconds
            date = new Date(timestamp < 10000000000 ? timestamp * 1000 : timestamp);
        } else {
            date = new Date(timestamp);
        }

        if (isNaN(date.getTime())) {
            console.error("Invalid timestamp for conversion:", timestamp);
            return null;
        }

        // IST is UTC+5:30, so add 5 hours and 30 minutes
        const istOffset = 5.5 * 60 * 60 * 1000; // 5.5 hours in milliseconds
        const istDate = new Date(date.getTime() + istOffset);
        return istDate;
    } catch (error) {
        console.error("Error converting GMT to IST:", error);
        return null;
    }
};

/**
 * Formats a timestamp to display time in IST (HH:MM AM/PM format)
 * @param {string|Date} timestamp - ISO timestamp string or Date object
 * @returns {string} Formatted time string like "10:30 AM" or "02:45 PM"
 */
export const formatMessageTime = (timestamp) => {
    if (!timestamp) return "";

    try {
        let date;
        if (timestamp instanceof Date) {
            date = timestamp;
        } else if (typeof timestamp === 'number') {
            // Heuristic: If timestamp is in seconds (10 digits), convert to milliseconds
            date = new Date(timestamp < 10000000000 ? timestamp * 1000 : timestamp);
        } else if (typeof timestamp === 'string') {
            date = new Date(timestamp);
        } else {
            return "";
        }

        if (isNaN(date.getTime())) {
            console.error("Invalid timestamp:", timestamp);
            return "";
        }

        // IST is UTC+5:30, so get UTC time and add 5.5 hours
        let hours = date.getUTCHours();
        let minutes = date.getUTCMinutes();

        // Add IST offset (5 hours 30 minutes)
        hours += 5;
        minutes += 30;

        // Handle minute overflow
        if (minutes >= 60) {
            hours += Math.floor(minutes / 60);
            minutes = minutes % 60;
        }

        // Handle hour overflow (24-hour format)
        if (hours >= 24) {
            hours = hours % 24;
        }

        // Determine AM/PM before converting to 12-hour format
        const ampm = hours >= 12 ? 'PM' : 'AM';

        // Convert to 12-hour format
        const displayHours = hours % 12 || 12; // 0 should be 12

        const minutesStr = String(minutes).padStart(2, '0');
        return `${displayHours}:${minutesStr} ${ampm}`;
    } catch (error) {
        console.error("Error formatting message time:", error);
        return "";
    }
};

/**
 * Formats a timestamp to a short date like "27 Apr"
 * @param {string|number|Date} timestamp 
 * @returns {string}
 */
export const formatShortDate = (timestamp) => {
    if (!timestamp) return "";
    try {
        let date;
        if (typeof timestamp === 'number') {
            date = new Date(timestamp < 10000000000 ? timestamp * 1000 : timestamp);
        } else {
            date = new Date(timestamp);
        }
        if (isNaN(date.getTime())) return "";

        const day = date.getDate();
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = months[date.getMonth()];
        return `${day} ${month}`;
    } catch (error) {
        return "";
    }
};

/**
 * Formats a timestamp to a full date with 24h time like "27 Apr 2026, 21:58"
 * @param {string|number|Date} timestamp 
 * @returns {string}
 */
export const formatFullDateWithTime = (timestamp) => {
    if (!timestamp) return "";
    try {
        let date;
        if (typeof timestamp === 'number') {
            date = new Date(timestamp < 10000000000 ? timestamp * 1000 : timestamp);
        } else {
            date = new Date(timestamp);
        }
        if (isNaN(date.getTime())) return "";

        const day = date.getDate();
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${day} ${month} ${year}, ${hours}:${minutes}`;
    } catch (error) {
        return "";
    }
};

/**
 * Groups a date into categories: Today, Previous 7 Days, Previous 30 Days, Older
 * @param {string|Date} timestamp - ISO timestamp string or Date object
 * @param {Object} labels - Optional localized labels { today, previous7Days, older, locale }
 * @returns {string} Group name
 */
export const getThreadGroup = (timestamp, labels = {}) => {
    const { 
        today = "Today", 
        previous7Days = "Previous 7 Days", 
        older = "Older",
        locale = "en-US"
    } = labels;

    if (!timestamp) return older;

    try {
        const date = new Date(timestamp);
        const now = new Date();
        const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const inputDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        const diffTime = todayDate - inputDate;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return today;
        if (diffDays <= 7) return previous7Days;

        // Use Intl.DateTimeFormat for localized month names
        const monthName = new Intl.DateTimeFormat(locale, { month: 'long' }).format(date);

        if (date.getFullYear() === now.getFullYear()) {
            return monthName;
        }
        return `${monthName} ${date.getFullYear()}`;
    } catch (error) {
        console.error("Error grouping thread date:", error);
        return older;
    }
};

/**
 * Formats current date for file exports in DD-MM-YY format
 * @returns {string} Formatted date string like "25-02-26"
 */
export const getExportFormattedDate = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const year = String(now.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
};

/**
 * Formats a Date object or timestamp into YYYY-MM-DDTHH:mm format suitable for datetime-local inputs
 * @param {Date|string|number} date - Date object or timestamp
 * @returns {string} Formatted date string
 */
export const formatDateTimeLocal = (date) => {
    if (!date) return "";

    try {
        const d = date instanceof Date ? date : new Date(date);
        if (isNaN(d.getTime())) return "";

        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        const hour = String(d.getHours()).padStart(2, "0");
        const minute = String(d.getMinutes()).padStart(2, "0");
        return `${year}-${month}-${day}T${hour}:${minute}`;
    } catch (error) {
        console.error("Error formatting datetime local:", error);
        return "";
    }
};

/**
 * Returns a formatted local datetime string (YYYY-MM-DDTHH:mm) for N days from today at 09:00
 * @param {number} days - Number of days from today
 * @returns {string} Formatted local datetime string
 */
export const getLocalDateTimeAfterDays = (days = 0) => {
    try {
        const date = new Date();
        date.setDate(date.getDate() + (Number(days) || 0));
        date.setHours(9, 0, 0, 0);
        return formatDateTimeLocal(date);
    } catch (error) {
        console.error("Error getting local datetime after days:", error);
        return "";
    }
};

/**
 * Formats a creation timestamp or date to "14 Oct 2026"
 * Supports ISO timestamp strings, Date objects, Unix timestamps, and YYYY-MM-DD
 * @param {string|number|Date} dateVal 
 * @returns {string} Formatted date like "14 Oct 2026"
 */
export const formatCreatedDate = (dateVal) => {
    if (!dateVal) return "";
    try {
        if (typeof dateVal === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateVal.trim())) {
            const [year, monthNum, day] = dateVal.trim().split("-");
            const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            const month = months[parseInt(monthNum, 10) - 1];
            return `${parseInt(day, 10)} ${month} ${year}`;
        }
        let date;
        if (typeof dateVal === "number") {
            date = new Date(dateVal < 10000000000 ? dateVal * 1000 : dateVal);
        } else {
            date = new Date(dateVal);
        }
        if (isNaN(date.getTime())) return String(dateVal);

        const day = date.getDate();
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    } catch (error) {
        return String(dateVal);
    }
};